import type { TeamMemberSubscription } from "@/types/team-subscription";

/** Formata descrição de pacote para exibição com quebras de linha legíveis. */
export function formatTeamSubscriptionDescription(description: string): string {
  let text = description.replace(/\r\n/g, "\n").trim();
  if (!text) return text;

  // Título com emojis (ex.: 🔥 Membro VIP 🔥) em linha separada do parágrafo
  text = text.replace(/(🔥[^🔥\n]+🔥)\s+/u, "$1\n\n");

  // Itens com ✅ — um por linha
  if (text.includes("✅")) {
    text = text.replace(/\s*✅\s*/g, "\n✅ ");
  }

  // CTA final em linha própria
  text = text.replace(/\s+(?=Seja VIP\b)/i, "\n\n");

  return text
    .replace(/^\n+/, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Janela em que a renovação antecipada via PIX é permitida (alinhada aos lembretes). */
export const SUBSCRIPTION_RENEWAL_WINDOW_DAYS = 7;

export function canRenewSubscriptionNow(
  sub: Pick<
    import("@/types/team-subscription").TeamMemberSubscription,
    "status" | "cancelAtPeriodEnd" | "currentPeriodEnd"
  >,
): boolean {
  if (sub.cancelAtPeriodEnd) return false;
  if (sub.status === "PENDING" || sub.status === "PAST_DUE") return true;
  if (sub.status === "EXPIRED" || sub.status === "CANCELLED") return true;
  if (sub.status === "ACTIVE" && sub.currentPeriodEnd) {
    const msUntilEnd = new Date(sub.currentPeriodEnd).getTime() - Date.now();
    return msUntilEnd <= SUBSCRIPTION_RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  }
  return false;
}

export function usesSubscriptionPayPage(
  sub: Pick<
    import("@/types/team-subscription").TeamMemberSubscription,
    "status"
  >,
): boolean {
  return sub.status === "PENDING" || sub.status === "PAST_DUE" || sub.status === "ACTIVE";
}

const formatSubscriptionDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "—";

/** Assinante com benefício ativo ou pagamento em andamento neste time. */
export function isActiveTeamSubscriber(
  sub: TeamMemberSubscription | null | undefined,
): sub is TeamMemberSubscription {
  return !!sub && ["ACTIVE", "PAST_DUE", "PENDING"].includes(sub.status);
}

/** Exibe lembrete de renovação / pagamento (sem CTA). */
export function shouldShowSubscriptionReminder(
  sub: TeamMemberSubscription | null | undefined,
): boolean {
  if (!isActiveTeamSubscriber(sub)) return false;
  if (sub.status === "PENDING" || sub.status === "PAST_DUE") return true;
  if (sub.status === "ACTIVE" && sub.cancelAtPeriodEnd) return true;
  if (sub.status === "ACTIVE" && sub.currentPeriodEnd) {
    const msUntilEnd = new Date(sub.currentPeriodEnd).getTime() - Date.now();
    return msUntilEnd <= SUBSCRIPTION_RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  }
  return false;
}

export function getSubscriptionExpiryMessage(
  sub: TeamMemberSubscription,
  daysUntilExpiry?: number | null,
  expiryDate?: string | null,
): string {
  const date = expiryDate ?? sub.currentPeriodEnd;

  if (sub.status === "PENDING") {
    return "Ativação pendente — informe os dados do cartão para ativar.";
  }
  if (sub.status === "PAST_DUE") {
    return sub.gracePeriodEndsAt
      ? `Cobrança em atraso — atualize o cartão até ${formatSubscriptionDate(sub.gracePeriodEndsAt)}.`
      : "Cobrança em atraso — atualize o cartão para manter o VIP.";
  }
  if (sub.status === "ACTIVE" && sub.cancelAtPeriodEnd) {
    return `Cancelamento agendado — benefícios até ${formatSubscriptionDate(sub.currentPeriodEnd)}.`;
  }
  if (daysUntilExpiry != null && daysUntilExpiry < 0) {
    return `Período encerrou em ${formatSubscriptionDate(date)} — aguardando renovação automática no cartão.`;
  }
  if (daysUntilExpiry === 0) {
    return `Período encerra hoje (${formatSubscriptionDate(date)}).`;
  }
  if (daysUntilExpiry != null && daysUntilExpiry > 0) {
    return `Próxima renovação em ${daysUntilExpiry} dia${daysUntilExpiry === 1 ? "" : "s"} (${formatSubscriptionDate(date)}).`;
  }
  return sub.currentPeriodEnd
    ? `Renovação automática em ${formatSubscriptionDate(sub.currentPeriodEnd)}.`
    : "Renovação automática mensal no cartão.";
}

export function getSubscriptionReminderMessage(sub: TeamMemberSubscription): string {
  const plan = sub.plan?.name ? ` (${sub.plan.name})` : "";

  if (sub.status === "PENDING") {
    return `Sua assinatura VIP${plan} aguarda ativação. Informe os dados do cartão em Minhas Assinaturas.`;
  }
  if (sub.status === "PAST_DUE") {
    return sub.gracePeriodEndsAt
      ? `Sua assinatura VIP${plan} está em atraso — atualize o cartão até ${formatSubscriptionDate(sub.gracePeriodEndsAt)}.`
      : `Sua assinatura VIP${plan} está em atraso. Atualize o cartão em Minhas Assinaturas.`;
  }
  if (sub.status === "ACTIVE" && sub.cancelAtPeriodEnd) {
    return `Sua assinatura VIP${plan} está cancelada — benefícios vencem em ${formatSubscriptionDate(sub.currentPeriodEnd)}.`;
  }
  if (sub.currentPeriodEnd) {
    const msUntilEnd = new Date(sub.currentPeriodEnd).getTime() - Date.now();
    const days = Math.ceil(msUntilEnd / (1000 * 60 * 60 * 24));
    if (days < 0) {
      return `Sua assinatura VIP${plan} aguarda renovação automática no cartão (período encerrou em ${formatSubscriptionDate(sub.currentPeriodEnd)}).`;
    }
    if (days === 0) {
      return `Sua assinatura VIP${plan} renova hoje (${formatSubscriptionDate(sub.currentPeriodEnd)}).`;
    }
    return `Sua assinatura VIP${plan} renova em ${days} dia${days === 1 ? "" : "s"} (${formatSubscriptionDate(sub.currentPeriodEnd)}).`;
  }
  return `Acompanhe sua assinatura VIP${plan} em Minhas Assinaturas.`;
}
